/**
 * The one way the chat asks the pane to open a prepared workflow's pre-launch
 * drawer (outlook-orachat-uplift s8, design C42, proof S7a -> S7b).
 *
 * The prepared card under the chat's answer dispatches it; App listens, checks
 * it, switches to Needs you and opens the drawer for the OPEN email only. The
 * event never starts anything: the drawer's Launch, pressed by the user, is the
 * only thing that runs the workflow.
 *
 * The event's name is spelled here and nowhere else in the add-in.
 */

/** The window event's name. */
export const OPEN_PRELAUNCH_EVENT = "ora:open-prelaunch";

/** Which brief the card was prepared on: the email and its triage row's mailbox. */
export interface OpenPrelaunchBriefRef {
  internet_message_id: string;
  mailbox_address: string | null;
}

/** The event's detail (version 1). */
export interface OpenPrelaunchDetail {
  v: 1;
  workflow_id: string;
  brief_ref: OpenPrelaunchBriefRef;
  /** The email the card was drawn for — always the brief's own email. */
  internet_message_id: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function nonEmpty(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

/**
 * A detail the pane may act on, or null. Null for anything but version 1 with
 * every field a string, and for a `brief_ref` that names another email than
 * `internet_message_id` (a card can only open its own brief).
 */
export function parseOpenPrelaunch(raw: unknown): OpenPrelaunchDetail | null {
  if (!isRecord(raw) || raw.v !== 1) return null;
  const { workflow_id: workflowId, internet_message_id: messageId, brief_ref: ref } = raw;
  if (!nonEmpty(workflowId) || !nonEmpty(messageId) || !isRecord(ref)) return null;
  const refMessageId = ref.internet_message_id;
  const mailbox = ref.mailbox_address;
  if (!nonEmpty(refMessageId) || refMessageId !== messageId) return null;
  if (mailbox !== undefined && mailbox !== null && typeof mailbox !== "string") return null;
  return {
    v: 1,
    workflow_id: workflowId,
    brief_ref: { internet_message_id: refMessageId, mailbox_address: mailbox || null },
    internet_message_id: messageId,
  };
}

/** Ask the pane to open this prepared workflow's pre-launch drawer. */
export function dispatchOpenPrelaunch(detail: OpenPrelaunchDetail, target: EventTarget = window): void {
  target.dispatchEvent(new CustomEvent(OPEN_PRELAUNCH_EVENT, { detail }));
}
